/**
 * DashboardReturnToast + useDashboardReturn — the dashboard side of the
 * "return to dashboard after a card is created or moved" rule
 * (CLAUDE.md §39; helper in src/utils/returnToDashboard.js).
 *
 * useDashboardReturn({ ready })
 *   Reads `location.state` on mount and whenever it changes. If it carries
 *   a toast, the toast is shown for ~3 s. If it carries a highlight, once
 *   the board has loaded (`ready`) the matching card is scrolled into view
 *   and given the `.inbox-return-highlight` class for ~2 s (a soft brand
 *   orange ring that fades — box-shadow only, so no reflow). The state is
 *   cleared immediately with a same-route replace so a browser refresh
 *   never replays either. A card that is not on this role's board (e.g.
 *   filtered out of the Inbox column, §36.7) is skipped silently.
 *
 * <DashboardReturnToast message={toast} />
 *   Minimal, non-blocking toast in the app's chrome style: dark teal
 *   surface, white text, brand tokens only, pointer-events none.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { readReturnState, cardSelector } from '../../utils/returnToDashboard';

const TOAST_MS = 3000;
const HIGHLIGHT_MS = 2200;
// The card may not be in the DOM the instant the board is ready (staggered
// entry animations, or a silent reload still in flight after the referral
// modal). Poll briefly, then give up silently.
const FIND_INTERVAL_MS = 150;
const FIND_TIMEOUT_MS = 5000;

export const RETURN_HIGHLIGHT_CLASS = 'inbox-return-highlight';

export function useDashboardReturn({ ready = true } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const toastTimer = useRef(null);

  // 1. Read the return state, then clear it at once (§39 / Step 3.2).
  useEffect(() => {
    const ret = readReturnState(location.state);
    if (!ret) return;
    if (ret.toast) {
      setToast(ret.toast);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
    }
    if (ret.highlight) setHighlight(ret.highlight);
    navigate(location.pathname, { replace: true, state: null });
    // location.state is the trigger; pathname and navigate are stable here.
  }, [location.state]); // eslint-disable-line

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // 2. Once the board is ready, find the card, scroll to it and flash it.
  useEffect(() => {
    if (!ready || !highlight) return undefined;
    const selector = cardSelector(highlight.cardType, highlight.cardId);
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let el = null;
    let elapsed = 0;
    let removeTimer = null;
    const poll = setInterval(() => {
      elapsed += FIND_INTERVAL_MS;
      el = document.querySelector(selector);
      if (el) {
        clearInterval(poll);
        try {
          el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
        } catch (e) {
          /* older browsers: ignore */
        }
        el.classList.add(RETURN_HIGHLIGHT_CLASS);
        removeTimer = setTimeout(() => {
          el.classList.remove(RETURN_HIGHLIGHT_CLASS);
          setHighlight(null);
        }, HIGHLIGHT_MS);
      } else if (elapsed >= FIND_TIMEOUT_MS) {
        clearInterval(poll); // not on this board — skip silently
        setHighlight(null);
      }
    }, FIND_INTERVAL_MS);
    return () => {
      clearInterval(poll);
      if (removeTimer) clearTimeout(removeTimer);
      if (el) el.classList.remove(RETURN_HIGHLIGHT_CLASS);
    };
  }, [ready, highlight]);

  return { toast };
}

export default function DashboardReturnToast({ message }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          key="dashboard-return-toast"
          role="status"
          aria-live="polite"
          style={styles.toast}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.25 }}
        >
          <CheckCircle2 size={16} color="var(--cp-success)" />
          <span>{message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const styles = {
  toast: {
    position: 'fixed',
    top: '84px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.7rem 1.2rem',
    background: 'var(--cp-primary-dark)',
    color: 'var(--cp-surface)',
    borderRadius: 'var(--radius)',
    fontFamily: "'Montserrat', var(--font-ui)",
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    zIndex: 2000,
    boxShadow: 'var(--cp-shadow-hover)',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
};
